/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { forwardRef, ReactElement, Ref, ComponentProps } from "react";
import { get } from "styled-system";
import { Box, BoxProps } from "../box";
import { useFormControl } from "../form/FormControl";
import { ITheme } from "../theme/theme";
import { useTheme } from "../theme/ThemeProvider";

type Variant = "outline" | "unstyled" | "flushed" | "filled";
type Size = "xs" | "sm" | "md" | "lg";

export interface IInput {
  /**
   * If `true`, the input will be disabled.
   * This sets `aria-disabled=true` and you can style this state by passing `_disabled` prop
   */
  disabled?: React.InputHTMLAttributes<any>["disabled"];
  /**
   * If `true`, the `input` will indicate an error.
   * This sets `aria-invalid=true` and you can style this state by passing `_invalid` prop
   *
   */
  invalid?: boolean;
  /**
   * If `true`, the input element will be required.
   */
  required?: React.InputHTMLAttributes<any>["required"];
  /**
   * If `true`, the input element will span the full width of it's parent
   */
  fullWidth?: boolean;
  /**
   * If `true`, prevents the value of the input from being edited.
   */
  readOnly?: React.InputHTMLAttributes<any>["readOnly"];
  /**
   * The visual size of the `input` element.
   */
  size?: Size;
  /**
   * The variant of the input style to use.
   */
  variant?: Variant;
  /**
   * The element or component to use in place of `input`
   */
  as?: any;
  /**
   * [ARIA] The accessible label to use, in scenarios where the input as no visible label
   */
  "aria-label"?: React.AriaAttributes["aria-label"];
  /**
   * [ARIA] The id of the element that describes the input.
   */
  "aria-describedby"?: React.AriaAttributes["aria-describedby"];

  /**
   * The border color when the input is focused. Use color keys in `theme.colors`
   * @example
   * focusBorderColor = "blue.500"
   */
  focusBorderColor?: string;

  /**
   * The border color when the input is invalid. Use color keys in `theme.colors`
   * @example
   * errorBorderColor = "red.500"
   */
  errorBorderColor?: string;
}

export type OmittedTypes = "size" | "disabled" | "required" | "checked" | "defaultChecked" | "readOnly";

const InputBox = Box.withComponent("input");

export type InputProps = IInput &
  Omit<ComponentProps<typeof InputBox>, "size" | "ref" | "as"> &
  React.RefAttributes<any>;

type ThemedInputProps = InputProps & { theme: ITheme };

export type InputSize = {
  fontSize: string;
  px: number;
  height: number;
};

const InputWithoutRef = (props: InputProps, ref: Ref<any>) => {
  const {
    size,
    variant,
    as,
    "aria-label": ariaLabel,
    "aria-describedby": ariaDescribedby,
    readOnly,
    fullWidth,
    disabled,
    invalid,
    required,
    focusBorderColor,
    errorBorderColor,
    ...rest
  } = props;
  const inputStyleProps = useInputStyle(props);
  const formControl = useFormControl(props);

  return (
    <InputBox
      ref={ref}
      as={as}
      _readOnly={formControl.readOnly}
      _disabled={formControl.disabled}
      required={formControl.required}
      readOnly={readOnly}
      aria-readonly={readOnly}
      aria-label={ariaLabel}
      aria-invalid={formControl.invalid}
      aria-required={formControl.required}
      aria-disabled={formControl.disabled}
      aria-describedby={ariaDescribedby}
      {...inputStyleProps}
      {...rest}
    />
  );
};

const Input = forwardRef(InputWithoutRef);

Input.displayName = "Input";

Input.defaultProps = {
  size: "md",
  as: "input",
  variant: "outline",
  fullWidth: true,
  borderColor: "gray.100",
  focusBorderColor: "blue.600",
  errorBorderColor: "red.600",
  rounded: 3,
};

export default Input;

function outlinedStyle({ theme, focusBorderColor, errorBorderColor }: any): BoxProps {
  /**
   * styled-system's get takes 3 args
   * - object or array to read from
   * - key to get
   * - fallback value
   */
  const _focusBorderColor = get(
    theme.colors,
    focusBorderColor || "",
    focusBorderColor || "", // If color doesn't exist in theme, use it's raw value
  );
  const _errorBorderColor = get(theme.colors, errorBorderColor || "", errorBorderColor || "");

  return {
    ...readOnly,
    border: "1px",
    borderColor: "inherit",
    bg: "white",
    _hover: {
      borderColor: "gray.300",
    },
    _disabled: {
      opacity: 0.4,
      cursor: "not-allowed",
    },
    _focus: {
      borderColor: _focusBorderColor,
    },
    _invalid: {
      borderColor: _errorBorderColor,
    },
  };
}

const readOnly: BoxProps = {
  _readOnly: {
    bg: "transparent",
    boxShadow: "none !important",
    userSelect: "all",
  },
};

const filledStyle = ({ theme, focusBorderColor, errorBorderColor }: any): BoxProps => {
  const _focusBorderColor = get(theme.colors, focusBorderColor || "", focusBorderColor || "");
  const _errorBorderColor = get(theme.colors, errorBorderColor || "", errorBorderColor || "");

  return {
    ...readOnly,
    border: "2px",
    borderColor: "transparent",
    bg: "gray.100",
    _hover: {
      bg: "gray.200",
    },
    _disabled: {
      opacity: 0.4,
      cursor: "not-allowed",
    },
    _focus: {
      zIndex: 1,
      bg: "transparent",
      borderColor: _focusBorderColor,
    },
    _invalid: {
      borderColor: _errorBorderColor,
    },
  };
};

const flushedStyle = ({ theme, focusBorderColor, errorBorderColor }: any): BoxProps => {
  const _focusBorderColor = get(theme.colors, focusBorderColor || "", focusBorderColor || "");
  const _errorBorderColor = get(theme.colors, errorBorderColor || "", errorBorderColor || "");

  return {
    ...readOnly,
    borderBottom: "2px",
    borderColor: "inherit",
    rounded: 0,
    px: undefined,
    bg: "transparent",
    height: undefined,
    _focus: {
      zIndex: 1,
      borderColor: _focusBorderColor,
    },
    _invalid: {
      borderColor: _errorBorderColor,
    },
  };
};

const unstyledStyle: BoxProps = {
  bg: "transparent",
  px: undefined,
  height: 0,
};

const variantProps = (props: ThemedInputProps): BoxProps | undefined => {
  switch (props.variant) {
    case "flushed":
      return flushedStyle(props);
    case "unstyled":
      return unstyledStyle;
    case "filled":
      return filledStyle(props);
    case "outline":
      return outlinedStyle(props);
    default:
      return undefined;
  }
};

const baseProps: BoxProps = {
  display: "flex",
  alignItems: "center",
  position: "relative",
  transition: "all 0.2s",
  outline: "none",
  appearance: "none",
  height: undefined,
};

export function getInputSizesProps(size: string | null | undefined): BoxProps {
  if (size === "lg") {
    return {
      fontSize: "lg",
      px: 4,
      height: 12,
    };
  }

  if (size === "sm") {
    return {
      fontSize: "sm",
      px: 3,
      height: 8,
    };
  }

  if (size === "xs") {
    return {
      fontSize: "sm",
      px: 4,
      height: 6,
    };
  }

  return {
    fontSize: "md",
    px: 4,
    height: 10,
  };
}

const sizeProps = (props: ThemedInputProps): BoxProps => getInputSizesProps(props.size || "md");

export const useInputStyle = (props: InputProps): any => {
  const theme = useTheme();

  const _props = { ...props, theme };

  return {
    width: props.fullWidth ? "100%" : undefined,
    ...baseProps,
    ...sizeProps(_props),
    ...variantProps(_props),
  };
};
